import { debounce } from 'lodash-es'
import { memo, useCallback, useEffect, useRef } from 'react'
import {
    setChatMinimized,
    setFullWindow,
} from '../../../../../../actions/layout'
import { sendPrivateMessage } from '../../../../../../actions/sendPrivateMessage'
import { sendRoomMessage } from '../../../../../../actions/sendRoomMessage'
import {
    closeDrawer,
    openWhiteboardDrawer,
} from '../../../../../../app-state/modals/state.drawer'
import { toggleFloaterVideo } from '../../../../../../app-state/modals/state.floater'
import { getScreenState } from '../../../../../../app-state/state.screen'
import {
    AppStateType,
    FloaterSize,
    RoomView,
    VideoType,
} from '../../../../../../constants/enums'
import { avatarUrl } from '../../../../../../mfcLogic/avatarPreviewUrl'
import {
    LAYOUTSTATE,
    updateLayoutState,
} from '../../../../../../state/state.layout'
import {
    VIDEOSTATE,
    activateTransform,
    addVideoTouchListeners,
    deactivateTransform,
    enableRoundedVideo,
    removeVideoTouchListeners,
} from '../../../../../../state/state.video'
import {
    closeWhiteboard,
    openWhiteboard,
} from '../../../../../../state/state.whiteboard'
import { isTouchDevice } from '../../../../../../utilities/browser'
import i18n from '../../../../../../utilities/i18n'
import { closeRoomAndNavBack } from '../../../../../../utilities/router'
import { safeObj } from '../../../../../../utilities/safe'
import ButtonShare from '../../../../../Buttons/ButtonShare'
import { useAppStateSelector } from '../../../../../Hooks/useAppState'
import { useLayoutSelector } from '../../../../../Hooks/useLayout'
import { usePortalStateSelector } from '../../../../../Hooks/usePortalState'
import { useRoomSelector } from '../../../../../Hooks/useRoom'
import { useUserSelector } from '../../../../../Hooks/useUser'
import { useVideoPlaying } from '../../../../../Hooks/useVideo'
import { useWhiteboardSelector } from '../../../../../Hooks/useWhiteboard'
import FlexGrid from '../../../../../common/FlexGrid'
import { palette } from '../../../../../styles/colors'
import shape from '../../../../../styles/shape'
import { COMPONENT_HEIGHTS } from '../../../../../theme'
import { Profile } from '../../../../Profile'
import ShareFreeAlbums from '../../../../ShareFreeAlbums'
import Video from '../../../../Video'
import Whiteboard, { WhiteboardRoot } from '../../../../Whiteboard'
import ChatActionsBar from '../../../ActionsBar'
import ChatHeader from '../../../ChatHeader'
import Compose from '../../../Compose'
import { calcVideoDimensions } from '../../../common/utilities'
import BackgroundFade from '../../BackgroundFade'
import Chat from '../../Chat'
import VideoPoster from '../../VideoPoster'
import {
    DRAG_INDICATOR,
    HEIGHT_OFFSET,
    MOBILE_CONVERSION_THRESHOLD,
    MOVEMENT_THRESHOLD,
    NO_COMPOSE_HEIGHT_OFFSET,
    SMALL_PREVIEW_SIZE,
    TOUCH_CONVERSION_THRESHOLD,
    ViewProps,
    animateActionBar,
    calcZoomedVideoHeight,
    getRightStyles,
    getVerticalChatHeight,
    moveZoomedVideoIcons,
    resetZoomedVideoIcons,
    resolveVerticalVideoHeight,
} from '../common/helpers'
import {
    AvatarBlurImage,
    AvatarImage,
    chatGridStyles,
    chatPaneStyles,
    gridRootCenter,
    nonFullscreenStyles,
    offlineButtonsStyles,
    shareAlbumsStyles,
    verticalRootStyles,
} from '../common/styles'

const Portrait = (props: ViewProps) => {
    const {
        id,
        size,
        roomView,
        height,
        width,
        disableKeyboardOpenDetection,

        showVideo,
        viewingTips,
        showZoomVideo,
        showLoungePreviews,
        surrogateName,
        isSurrogateBroadcasting,
        noProfileVideo,
        noVideoDefault,

        setLoungePreviews,
        setZoomVideo,
        toggleTips,
    } = props

    const rootRef = useRef<HTMLDivElement>(null)
    const chatGridContainerRef = useRef<HTMLDivElement>(null)
    const actionsBarRef = useRef<HTMLDivElement>(null)
    const showingActionBar = useRef<boolean>(true)
    // these could be moved up a level if multiple views offered zoom
    const zoomTouchPosition = useRef<number>(null)
    const zoomTouchStartPosition = useRef<number>(null)
    const touchScratchPadRef = useRef<{
        // bounds of video container at start
        bounds: DOMRect
        // previous percentage to bottom value
        pPercent: number
        // previous scale factor (what was used last movement)
        pScale: number
        // starting y of the drag
        startY: number
        // starting x of the drag
        startX: number
        // if user has moved enough to imply drag intention
        moveThreshold: boolean
        // if user has dragged far enough to convert to preview
        transitionThreshold: boolean
        // state of mobile search header at drag start
        expandedSearchMobile: boolean
        refs: {
            backgroundFadeCanvas: HTMLElement
            chatHeaderContainer: HTMLElement
            floatingRoomVideoContainer: HTMLElement
            pullDownIconContainer: HTMLElement
            // transitionCover: HTMLElement
            offlineShareButton: HTMLElement
        }
    }>(null)
    const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')
    const isIOS = useAppStateSelector(AppStateType.APP, 'isIOS')
    const isLounge = useRoomSelector(id, 'isLounge')
    const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')
    const roomOpened = useRoomSelector(id, 'viewing')
    const isWhiteboardOpen = useWhiteboardSelector('open')

    const hasWhiteboard = useUserSelector(id, 'hasWhiteboard')
    const isModel = useUserSelector(id, 'isModel') || isLounge
    const isOffline = useUserSelector(id, 'isOffline')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isPublicShow = useUserSelector(id, 'isPublicShow')
    const isAway = useUserSelector(id, 'isAway')
    const clubShowOp = useUserSelector(id, 'clubShow', {}, true, 'op')
    const name = useUserSelector(id, 'name')
    const shareAlbums = useUserSelector(id, 'shareAlbums')
    const shareFollows = useUserSelector(id, 'shareFollows')
    const shareFreeAlbums = useUserSelector(id, 'shareFreeAlbums')
    const shareRecordings = useUserSelector(id, 'shareRecordings')
    const shareThings = useUserSelector(id, 'shareThings')
    const shareTipmenus = useUserSelector(id, 'shareTipmenus')
    const slug = useUserSelector(id, 'slug')
    const videoPlaying = useVideoPlaying(id)

    const isPM = roomView === RoomView.PM
    const isProfile = roomView === RoomView.Profile
    const isFullscreen = size === FloaterSize.Fullscreen

    const modelName = !isLounge ? name : surrogateName
    const modelID = !isLounge ? id : loungeSurrogateID
    const isModelBroadcasting = isLounge
        ? isSurrogateBroadcasting
        : isBroadcasting

    const hasShare =
        shareAlbums + shareRecordings + shareTipmenus + shareThings > 0 ||
        shareFollows > 0

    const composeFocused = usePortalStateSelector('composeFocused')
    const quietFocus = usePortalStateSelector('quietFocus')
    const minimized = useLayoutSelector('minimized')
    const pinToTop = composeFocused && !quietFocus // isIOS &&
    const { height: videoHeight = 0 } = !isFullscreen
        ? { height }
        : calcVideoDimensions(width, height, true, false)

    const rightStyles = getRightStyles({
        vertical: true,
        isModel,
    })

    // don't show zoom video if:
    // - we're banned/kicked
    // - model is offline or not broadcasting
    // - we're not vertical
    // - we've got the compose open with pin to top
    // - we're not mobile
    const canShowZoomVideo =
        (!isOffline || (isLounge && modelID)) &&
        isMobile &&
        isFullscreen &&
        isModelBroadcasting &&
        !pinToTop &&
        !isProfile
    // video size calculations
    // - keep the zoomed video calculation separate so it can be used to
    //   determine whether its sane to offer the option
    const zoomedVideoHeight = calcZoomedVideoHeight(height, width)
    const calcVideoHeight =
        showZoomVideo && canShowZoomVideo
            ? zoomedVideoHeight
            : resolveVerticalVideoHeight({
                  isFullscreen,
                  pinToTop,
                  height,
                  width,
                  size,
              })
    const calcVideoWidth = (calcVideoHeight * 16) / 9
    // determine whether or not we have enough room height to even bother zooming
    const canChangeVideoHeight =
        showZoomVideo || Math.abs(zoomedVideoHeight - calcVideoHeight) >= 36
    const zoomVideo = canChangeVideoHeight && showZoomVideo && canShowZoomVideo

    const verticalChatHeight = getVerticalChatHeight({
        isMobile,
        height,
        videoHeight,
        isOffline,
        isBroadcasting,
        isPM,
        showVideo,
    })

    useEffect(() => {
        if (!composeFocused && chatGridContainerRef.current) {
            chatGridContainerRef.current.scrollTop = 0
        }
    }, [composeFocused])

    const onToggleStream = useCallback(() => {
        if (!isBroadcasting) {
            setFullWindow(false)
        }

        toggleFloaterVideo()
    }, [isBroadcasting])

    // whiteboard
    const onCloseWhiteboard = useCallback(() => {
        closeWhiteboard()
        if (chatGridContainerRef.current) {
            chatGridContainerRef.current.scrollTop = 0
        }
        if (!showingActionBar.current) animateActionBar(actionsBarRef.current)

        showingActionBar.current = true
    }, [])
    const scrollWhiteboard = useCallback(() => {
        if (isProfile) {
            chatGridContainerRef.current.scroll({
                top: 0,
                behavior: 'smooth',
            })
        } else
            chatGridContainerRef.current.scroll({
                top: verticalChatHeight,
                behavior: 'smooth',
            })
    }, [height, videoHeight, verticalChatHeight, isProfile])
    const onToggleWhiteboard = useCallback(() => {
        if (zoomVideo || isProfile) {
            if (isWhiteboardOpen) {
                closeDrawer(true)
                closeWhiteboard()
            } else {
                openWhiteboardDrawer(modelName)
            }
            return
        }
        if (isWhiteboardOpen) {
            onCloseWhiteboard()
        } else {
            if (actionsBarRef.current) {
                actionsBarRef.current.style.height = '0px'
                actionsBarRef.current.style.maxHeight = '0px'
                actionsBarRef.current.style.minHeight = '0px'
                actionsBarRef.current.style.opacity = '0'
                actionsBarRef.current.style.pointerEvents = 'none'
            }
            showingActionBar.current = false
            openWhiteboard(modelName)
        }
    }, [isWhiteboardOpen, isProfile, modelName, zoomVideo])

    // chat container scroll
    const onVerticalChatContainerScroll = useCallback(
        debounce((event) => {
            const scrollTop = event.target.scrollTop
            const usableScrollTop = Math.ceil(scrollTop)
            const scrolledSlightly = usableScrollTop < verticalChatHeight
            // this should only animate when actionbar is not present currently and:
            // 1) whiteboard close button is tapped
            // 2) we scroll the chat pane
            // console.log('[onVerticalChatContainerScroll]', {
            //     scrollTop,
            //     verticalChatHeight,
            //     scrolledSlightly,
            //     showingActionBar: showingActionBar.current,
            // })
            if (scrolledSlightly && !showingActionBar.current) {
                if (actionsBarRef.current) {
                    animateActionBar(actionsBarRef.current)
                    showingActionBar.current = true
                }
            }
        }, 150),
        [verticalChatHeight],
    )

    // video drag down handlers
    const videoTouchStart = useCallback(
        (event: TouchEvent) => {
            event.stopPropagation()
            event.preventDefault()
            // console.log('[FloatingPortal->onDragStart]', { id })
            if (!rootRef.current) return
            deactivateTransform(modelID)

            // keep track of whether we started dragging with it open or not
            const mobileSearch = LAYOUTSTATE.layout.expandedSearchMobile
            // close header search
            if (mobileSearch) updateLayoutState({ expandedSearchMobile: false })
            const floatingRoomVideoContainer = rootRef.current.querySelector(
                `#inline-video-container-${modelID}`,
            ) as HTMLElement

            const videoContainer = VIDEOSTATE[modelID].container
            const shouldShowPullDownIndicator =
                videoContainer &&
                !DRAG_INDICATOR.shown &&
                !document.querySelector('.vjs-click-to-unmute-container')
            const pullDownIconContainer = shouldShowPullDownIndicator
                ? document.createElement('span')
                : null
            if (shouldShowPullDownIndicator) {
                pullDownIconContainer.classList.add('video-pull-down-icon')
                videoContainer.style.borderRadius = `${shape.borderRadius}px`
                DRAG_INDICATOR.shown = true
                const pullDownIcon = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'svg',
                )
                pullDownIcon.setAttribute('viewBox', '0 0 24 24')
                const pullDownPath = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'path',
                )
                // from mdi
                pullDownPath.setAttribute(
                    'd',
                    'M12,10A2,2 0 0,0 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12A2,2 0 0,0 12,10M8,17L12,21.5L16,17H8Z',
                )
                pullDownIcon.appendChild(pullDownPath)
                pullDownIconContainer.appendChild(pullDownIcon)
                videoContainer.appendChild(pullDownIconContainer)
            }
            const { clientY, clientX } = event.touches[0]
            const backgroundFadeCanvas = rootRef.current.querySelector(
                '#background-fade canvas',
            ) as HTMLElement
            // const transitionCover = document.querySelector(
            //     '#transition-cover',
            // ) as HTMLElement
            const offlineShareButton = document.querySelector(
                '#offline-share-button',
            ) as HTMLElement
            // the lounge chat header uses the lounge id
            const chatHeaderContainer = rootRef.current.querySelector(
                `#chat-header-${id}`,
            ) as HTMLElement
            touchScratchPadRef.current = {
                startY: clientY,
                startX: clientX,
                pPercent: 0,
                pScale: 0,
                bounds: videoContainer.getBoundingClientRect(),
                moveThreshold: false,
                transitionThreshold: false,
                expandedSearchMobile: mobileSearch,
                refs: {
                    backgroundFadeCanvas,
                    chatHeaderContainer,
                    floatingRoomVideoContainer,
                    pullDownIconContainer,
                    // transitionCover,
                    offlineShareButton,
                },
            }
        },
        [modelID],
    )
    const videoTouchMove = useCallback(
        (event: TouchEvent) => {
            event.stopPropagation()
            event.preventDefault()
            const { touches } = event
            const { pageY, clientX, clientY } = touches[0]
            if (!touchScratchPadRef.current || !rootRef.current) return
            // use the room dimensions instead of the window dimensions for calculating the
            // video drag down scaling/location
            const { roomWidth, roomHeight } = getScreenState()
            const {
                startY: touchStartY,
                startX: touchStartX,
                pPercent,
                pScale,
                bounds,
                expandedSearchMobile,
                refs: {
                    backgroundFadeCanvas,
                    chatHeaderContainer,
                    floatingRoomVideoContainer,
                    pullDownIconContainer,
                    offlineShareButton,
                },
            } = touchScratchPadRef.current
            const percentageToBottom = pageY / roomHeight
            const shrinking = percentageToBottom < pPercent
            touchScratchPadRef.current.pPercent = percentageToBottom
            const alreadyScaled = pScale > 0
            const dragPercentY = Math.abs(clientY - touchStartY) / roomHeight

            // if we move a bit since starting we're probably intending to drag the video
            if (
                dragPercentY < MOVEMENT_THRESHOLD &&
                !shrinking &&
                !alreadyScaled
            )
                return

            if (pullDownIconContainer && pullDownIconContainer.isConnected)
                pullDownIconContainer.remove()
            const currentMoveThresholdMet =
                touchScratchPadRef.current.moveThreshold
            if (!currentMoveThresholdMet) {
                const floatingRoomPortal = rootRef.current
                floatingRoomPortal.style.background = 'transparent'

                const floatingRoomActionBar = actionsBarRef.current
                floatingRoomActionBar.style.opacity = '0'

                const floatingRoomChatContainer = chatGridContainerRef.current
                floatingRoomChatContainer.style.opacity = '0'

                if (backgroundFadeCanvas) {
                    // backgroundFadeCanvas.style.opacity = '0'
                    backgroundFadeCanvas.style.display = 'none'
                }
                if (offlineShareButton) {
                    offlineShareButton.style.opacity = '0'
                }
                chatHeaderContainer.style.opacity = '0'

                floatingRoomVideoContainer.style.background = 'transparent'
            }

            touchScratchPadRef.current.moveThreshold = true
            const threshold = isMobile
                ? MOBILE_CONVERSION_THRESHOLD
                : TOUCH_CONVERSION_THRESHOLD
            touchScratchPadRef.current.transitionThreshold =
                percentageToBottom > threshold
            const videoContainer = VIDEOSTATE[modelID].container
            if (videoContainer) {
                const { width, height, x: startingX, y: startingY } = bounds

                const { width: currentWidth } =
                    videoContainer.getBoundingClientRect()
                const adjustedPercentage =
                    percentageToBottom + (roomWidth < 600 ? 0 : 0.15)
                // only scale further if we're not min size or if we're growing
                const scaleFactor = 1 - adjustedPercentage //SMALL_PREVIEW_SIZE / currentWidth
                const shouldScale =
                    currentWidth > SMALL_PREVIEW_SIZE() || scaleFactor > pScale
                // make some space for a users finger, so based on the starting side,
                // shift the x value to avoid the finger in a sensible way
                const fromRight = touchStartX >= roomWidth / 1.5
                const fromLeft = touchStartX <= roomWidth / 3
                const fromCenter = !fromLeft && !fromRight
                const x =
                    clientX -
                    (fromCenter
                        ? width / 2 // center
                        : fromLeft
                          ? width * 0.25 // left
                          : width * 0.75) // right
                const y =
                    clientY -
                    height / 2 -
                    (expandedSearchMobile
                        ? COMPONENT_HEIGHTS.headerCompact +
                          COMPONENT_HEIGHTS.inputSearch +
                          24 // DG: not sure why this needs an extra 16px to line up right
                        : 0)

                if (shouldScale) {
                    touchScratchPadRef.current.pScale = scaleFactor
                    videoContainer.style.transform = `translate3d(${
                        startingX + x
                    }px, ${startingY + y}px, 0) scale(${scaleFactor})`
                    floatingRoomVideoContainer.style.transform = `translate3d(${
                        startingX + x
                    }px, ${startingY + y}px, 0) scale(${scaleFactor})`
                } else {
                    videoContainer.style.transform = `translate3d(${
                        startingX + x
                    }px, ${startingY + y}px, 0) scale(${pScale})`
                    floatingRoomVideoContainer.style.transform = `translate3d(${
                        startingX + x
                    }px, ${startingY + y}px, 0) scale(${pScale})`
                }

                // account for the shrinking effect of values caused by scaling
                const scalar = 1 / pScale
                videoContainer.style.borderRadius = `${
                    shape.borderRadius * scalar
                }px`
                floatingRoomVideoContainer.style.borderRadius = `${
                    shape.borderRadius * scalar
                }px`
                if (touchScratchPadRef.current.transitionThreshold) {
                    videoContainer.style.animation = '0.4s ease-out shadowPulse'
                    videoContainer.style.boxShadow = `0px ${1.5 * scalar}px ${
                        3 * scalar
                    }px ${palette.layerShadow}, 0px ${2 * scalar}px ${
                        5 * scalar
                    }px ${palette.layerSecondaryShadow}`
                } else {
                    videoContainer.style.animation = null
                    videoContainer.style.boxShadow = null
                }
            }
        },
        [modelID],
    )
    const videoTouchEnd = useCallback(
        (event: TouchEvent) => {
            const { changedTouches } = event
            if (
                !touchScratchPadRef.current ||
                (changedTouches && changedTouches.length < 1) ||
                !rootRef.current
            )
                return
            const {
                startX,
                startY,
                transitionThreshold,
                refs: {
                    backgroundFadeCanvas,
                    floatingRoomVideoContainer,
                    chatHeaderContainer,
                    // transitionCover,
                    offlineShareButton,
                },
            } = touchScratchPadRef.current

            const floatingRoomPortal = rootRef.current
            floatingRoomPortal.style.background = null
            const floatingRoomActionBar = actionsBarRef.current
            if (showingActionBar.current)
                floatingRoomActionBar.style.opacity = '1'
            const floatingRoomChatContainer = chatGridContainerRef.current
            floatingRoomChatContainer.style.opacity = '1'
            if (backgroundFadeCanvas) {
                // backgroundFadeCanvas.style.opacity = null
                backgroundFadeCanvas.style.display = null
            }
            if (offlineShareButton) {
                offlineShareButton.style.opacity = null
            }
            chatHeaderContainer.style.opacity = '1'
            // if (transitionCover) {
            //     transitionCover.style.opacity = '1'
            //     transitionCover.style.animation = 'none'
            // }
            floatingRoomVideoContainer.style.background = 'unset'
            floatingRoomVideoContainer.style.transform = 'unset'
            floatingRoomVideoContainer.style.borderRadius = 'unset'
            const videoContainer = VIDEOSTATE[modelID].container

            if (transitionThreshold) {
                // use the current transformed videoContainer bounds
                //  to set the floater location coords
                const { x, y } = safeObj(
                    videoContainer?.getBoundingClientRect(),
                )
                // activateTransform(modelID)
                // convertFloatingRoomToSticky(id, { x, y }, true)
                closeRoomAndNavBack(false, { x, y })
            }
            touchScratchPadRef.current = null

            if (videoContainer) {
                videoContainer.style.transform = 'unset'
                videoContainer.style.borderRadius = 'unset'
                videoContainer.style.boxShadow = null
                videoContainer.style.background = 'unset'
                setTimeout(() => {
                    videoContainer
                        ?.querySelectorAll('.video-pull-down-icon')
                        .forEach((el) => el.remove())
                }, 1000)
            }
            if (!transitionThreshold) activateTransform(modelID)
            const { clientX, clientY } = changedTouches[0]
            if (
                Math.abs(clientX - startX) < 5 &&
                Math.abs(clientY - startY) < 5
            ) {
                videoContainer.click()
            }
        },
        [id, modelID],
    )

    const zoomTouchStart = useCallback(
        (event: TouchEvent) => {
            event.preventDefault()
            const { touches } = event
            const { clientX } = touches[0]
            zoomTouchStartPosition.current = clientX
            if (zoomTouchPosition.current === null) {
                zoomTouchPosition.current = (width - calcVideoWidth) / 2
            }
        },
        [width, calcVideoWidth],
    )
    const zoomTouchMove = useCallback(
        (event: TouchEvent) => {
            event.preventDefault()
            const { touches } = event
            const { clientX } = touches[0]
            const translationAmount = clientX - zoomTouchStartPosition.current

            if (translationAmount > 0) {
                // we're moving the video to the right
                zoomTouchPosition.current = Math.min(
                    zoomTouchPosition.current + translationAmount,
                    0,
                )
            } else {
                // we're moving the video to the left
                zoomTouchPosition.current = Math.max(
                    zoomTouchPosition.current + translationAmount,
                    width - calcVideoWidth,
                )
            }

            const zoomedInlineVideoContainer = rootRef.current.querySelector(
                `#inline-video-container-${modelID}`,
            ) as HTMLDivElement

            moveZoomedVideoIcons(
                Math.max(
                    calcVideoWidth - width + zoomTouchPosition.current,
                    isIOS ? 96 : 40,
                ),
                -zoomTouchPosition.current,
            )
            if (zoomedInlineVideoContainer) {
                zoomedInlineVideoContainer.style.translate = `${zoomTouchPosition.current}px`
            }
            zoomTouchStartPosition.current = clientX
        },
        [modelID, width, calcVideoWidth],
    )

    const setShowZoom = useCallback(() => {
        const newZoomVideo = !showZoomVideo
        const calcVideoHeight = newZoomVideo
            ? calcZoomedVideoHeight(height, width)
            : resolveVerticalVideoHeight({
                  isFullscreen,
                  pinToTop,
                  height,
                  width,
                  size,
              })
        const calcVideoWidth = (calcVideoHeight * 16) / 9
        zoomTouchPosition.current = (width - calcVideoWidth) / 2
        setZoomVideo(() => newZoomVideo)
    }, [showZoomVideo, isFullscreen, pinToTop, height, width, size])
    // handle video rounding css
    useEffect(() => {
        // current places <Video> is used where it should be rounded:
        // 1) small sticky preview needs all rounded corners
        // 2) medium/large sticky preview needs bottom corners rounded
        const elevated = !isModelBroadcasting
            ? false
            : isFullscreen && !noProfileVideo
        const rounded = !isFullscreen

        if (modelID) {
            enableRoundedVideo(modelID, rounded, elevated, size, zoomVideo)
            if (zoomVideo) {
                moveZoomedVideoIcons(
                    Math.max(
                        calcVideoWidth - width + (width - calcVideoWidth) / 2,
                        isIOS ? 96 : 40,
                    ),
                    -((width - calcVideoWidth) / 2),
                )
            }
            return () => {
                resetZoomedVideoIcons()
                enableRoundedVideo(modelID, false, false, size, false)
            }
        }
    }, [
        modelID,
        size,
        zoomVideo,
        videoPlaying,
        isModelBroadcasting,
        noProfileVideo,
        clubShowOp,
        isAway,
        isPublicShow,
    ])
    useEffect(() => {
        zoomTouchPosition.current = null
        if (
            roomOpened && // only allow drag to preview when the room is actually opened
            modelID &&
            isFullscreen &&
            showVideo &&
            !composeFocused
        ) {
            if (zoomVideo) {
                chatGridContainerRef.current
                    .querySelector(`#room-chat-${id}`)
                    ?.scroll?.({
                        top: 10000,
                        behavior: 'instant',
                    })
                moveZoomedVideoIcons(
                    Math.max(
                        calcVideoWidth - width + (width - calcVideoWidth) / 2,
                        isIOS ? 96 : 40,
                    ),
                    -((width - calcVideoWidth) / 2),
                )
                addVideoTouchListeners(modelID, {
                    onTouchStart: zoomTouchStart,
                    onTouchMove: zoomTouchMove,
                })
            } else {
                activateTransform(modelID)
                addVideoTouchListeners(modelID, {
                    onTouchStart: videoTouchStart,
                    onTouchMove: videoTouchMove,
                    onTouchEnd: videoTouchEnd,
                })
            }
            return () => {
                removeVideoTouchListeners(modelID)
            }
        } else {
            removeVideoTouchListeners(modelID)
        }
    }, [
        modelID,
        roomOpened,
        isFullscreen,
        composeFocused,
        showVideo,
        zoomVideo,
    ])

    const Header = (
        <ChatHeader
            id={id}
            preview={size < FloaterSize.Fullscreen}
            showBadge={isProfile || (isPM && !showVideo)}
            isProfile={isProfile}
            pm={isPM}
            room
        />
    )

    const ActionsBar = (
        <ChatActionsBar
            id={id}
            roomView={roomView}
            ref={actionsBarRef}
            video={!noVideoDefault ? showVideo : undefined}
            onWhiteboard={onToggleWhiteboard}
            onVideoToggle={!noVideoDefault ? onToggleStream : undefined}
            floating
        />
    )

    const ChatInputBar = (
        <Compose
            hide={minimized}
            id={id}
            onSend={isPM ? sendPrivateMessage : sendRoomMessage}
            // onSetComposeFocused={onSetComposeFocused}
            // onFocused={onFocused}
            // setFullWindow={setFullWindow}
            showZoomVideo={
                canShowZoomVideo && canChangeVideoHeight
                    ? showZoomVideo
                    : undefined
            }
            setZoomVideo={setShowZoom}
            setMinimized={setChatMinimized}
            placeholder={i18n.t(
                isPM ? 'chat.sendPrivateMessage' : 'chat.sendChatMessage',
            )}
            hideTipButton={!isModel}
            disableKeyboardOpenDetection={disableKeyboardOpenDetection}
            // overrideHeight={height}
            pm={isPM}
        />
    )

    const showFade = (isMobile || isTouchDevice) && isFullscreen
    // console.log(
    //     '[FloatingPortal.Portrait]',
    //     { modelID, slug },
    //     {
    //         canShowZoomVideo,
    //         canChangeVideoHeight,
    //         showZoomVideo,
    //         zoomVideo,
    //         showVideo,
    //         noProfileVideo,
    //         // division,
    //         // showFade,
    //         // isMobile,
    //         // isTouchDevice,
    //         // vertical,
    //         // isFullscreen,
    //     },
    //     {
    //         zoomedVideoHeight,
    //         calcVideoHeight,
    //         calcVideoWidth,
    //         // width,
    //         // height,
    //         // size,
    //     },
    // )

    // this should maybe just be set when it's toggled?
    const avatarSrc = avatarUrl(id, 300)

    return (
        <div
            css={[
                isFullscreen ? verticalRootStyles : nonFullscreenStyles,
                !isModel && isProfile && gridRootCenter,
            ]}
            ref={rootRef}
        >
            {/* [not fullscreen] ChatHeader is above video */}
            {!isFullscreen && size > FloaterSize.Small && Header}
            {/* [portrait] fade only in portrait */}
            {showFade && (
                <BackgroundFade
                    id={modelID}
                    width={calcVideoWidth}
                    height={calcVideoHeight}
                    top={89}
                    left={0}
                    filter={
                        darkMode
                            ? 'blur(46px) saturate(150%) opacity(34%) brightness(105%)'
                            : 'blur(46px) saturate(150%) opacity(28%) brightness(165%)'
                    }
                />
            )}
            {/* [Video Section] we're in a model's room or one of the lounges */}
            {isModel && (
                <>
                    {/* in a model's room and allowed to view her stream or in the lounge */}
                    {/* show mfc share button if the model is offline and has share content */}
                    {!isLounge && hasShare && isOffline && isFullscreen && (
                        <div
                            id="offline-share-button"
                            css={offlineButtonsStyles}
                            style={{
                                top: noProfileVideo
                                    ? Math.min(
                                          calcVideoWidth, // profile pics are square
                                          zoomedVideoHeight,
                                      ) - 56
                                    : calcVideoHeight - 56,
                            }}
                        >
                            <ButtonShare slug={slug} />
                        </div>
                    )}
                    {/* hide video if in the lounge with no model selected */}
                    {isLounge && !loungeSurrogateID && !isProfile && (
                        <VideoPoster />
                    )}
                    {(!isLounge || !!loungeSurrogateID || isProfile) && (
                        <>
                            {!noProfileVideo && (
                                <Video
                                    containerStyles={{
                                        height: calcVideoHeight,
                                        minHeight: calcVideoHeight,
                                        maxHeight: calcVideoHeight,
                                        width: calcVideoWidth,
                                        flexBasis: calcVideoWidth,
                                        flexGrow: 0,
                                        flexShrink: 1,
                                        background: isFullscreen
                                            ? palette.backgroundLightGray
                                            : null,
                                        translate: zoomVideo
                                            ? (zoomTouchPosition.current ??
                                              (width - calcVideoWidth) / 2)
                                            : 0,
                                    }}
                                    id={modelID}
                                    compactPreviewLabel={
                                        size === FloaterSize.Small
                                    }
                                    extendedPreviewLabel={
                                        size === FloaterSize.Fullscreen &&
                                        !zoomVideo
                                    }
                                    videoType={
                                        // VideoType.Room
                                        isFullscreen
                                            ? VideoType.Room
                                            : VideoType.StickyPreview
                                    }
                                    pinToTop={isFullscreen && pinToTop}
                                    allowControls={isFullscreen}
                                />
                            )}
                            <AvatarImage
                                src={avatarSrc}
                                crossOrigin="anonymous"
                                height={zoomedVideoHeight}
                                data-testid="Container__AvatarImage"
                                // onClick={}
                                // style={opaqueStyle}
                                style={
                                    noProfileVideo
                                        ? {
                                              width,
                                              height: zoomedVideoHeight,
                                              opacity: 1,
                                          }
                                        : null
                                }
                            />
                            <AvatarBlurImage
                                src={avatarSrc}
                                crossOrigin="anonymous"
                                height={zoomedVideoHeight}
                                style={
                                    noProfileVideo
                                        ? {
                                              top: 88,
                                              width,
                                              height: zoomedVideoHeight,
                                              opacity: 1,
                                              filter: darkMode
                                                  ? 'blur(46px) saturate(150%) opacity(34%) brightness(105%)'
                                                  : 'blur(46px) saturate(150%) opacity(28%) brightness(165%)',
                                          }
                                        : null
                                }
                            />
                        </>
                    )}
                    {/* [portrait] ChatHeader is below video */}
                    {!(isLounge && isProfile) && isFullscreen && Header}
                    {/* [portrait] chat actions bar is below the video */}
                    {!(isLounge && isProfile) &&
                        isFullscreen &&
                        isModel &&
                        ActionsBar}
                </>
            )}
            {/* [Chat Section] if we're allowed in */}
            {isFullscreen && !isProfile && (
                <FlexGrid
                    ref={chatGridContainerRef}
                    overrides={chatGridStyles}
                    style={{
                        flexBasis: rightStyles,
                        maxWidth: rightStyles,
                        // overflowX: 'hidden',
                        overflowY:
                            isMobile || isWhiteboardOpen ? 'scroll' : 'hidden',
                    }}
                    data-testid="Chat__Section"
                    onScroll={onVerticalChatContainerScroll}
                >
                    <div
                        css={chatPaneStyles}
                        style={{
                            height: height > 0 ? 'auto' : '100%',
                        }}
                        data-testid="Chat__Container"
                    >
                        <Chat
                            key={`chat-${id}`}
                            id={id}
                            height={height}
                            showingTips={viewingTips}
                            onTipToggle={toggleTips}
                            onOpenWhiteboard={onToggleWhiteboard}
                            styleHeight={
                                height > 0 && calcVideoHeight > 0
                                    ? showLoungePreviews
                                        ? height -
                                          NO_COMPOSE_HEIGHT_OFFSET -
                                          calcVideoHeight
                                        : height -
                                          HEIGHT_OFFSET -
                                          calcVideoHeight
                                    : '100%'
                            }
                            showLoungePreviews={showLoungePreviews}
                            setLoungePreviews={setLoungePreviews}
                            pm={isPM}
                        />
                        {!showLoungePreviews && ChatInputBar}
                    </div>
                    {/* [portrait] display any free mfc share albums below the chat panel (scroll the entire chat panel up) */}
                    {shareFreeAlbums > 0 &&
                        !isWhiteboardOpen &&
                        !zoomVideo &&
                        !isProfile && (
                            <div css={shareAlbumsStyles}>
                                <ShareFreeAlbums
                                    name={name}
                                    width={width}
                                    albums={shareFreeAlbums}
                                />
                            </div>
                        )}
                    {/* [portrait] whiteboard may be accessed by scrolling the entire chat panel up */}
                    {hasWhiteboard && isWhiteboardOpen && !zoomVideo && (
                        <WhiteboardRoot>
                            <Whiteboard
                                identifier={slug}
                                showAvatar={false}
                                scrollIntoView={scrollWhiteboard}
                                onClose={onCloseWhiteboard}
                            />
                        </WhiteboardRoot>
                    )}
                </FlexGrid>
            )}
            {isFullscreen && isProfile && (
                <FlexGrid
                    ref={chatGridContainerRef}
                    overrides={chatGridStyles}
                    style={{
                        flexBasis: rightStyles,
                        maxWidth: rightStyles,
                        overflowY: 'scroll',
                    }}
                    // xs={12}
                    data-testid="Chat__Section"
                    // item
                    onScroll={onVerticalChatContainerScroll}
                >
                    {hasWhiteboard && isWhiteboardOpen && (
                        <WhiteboardRoot>
                            <Whiteboard
                                identifier={slug}
                                showAvatar={false}
                                scrollIntoView={scrollWhiteboard}
                                onClose={onCloseWhiteboard}
                            />
                        </WhiteboardRoot>
                    )}
                    <Profile slugOrId={id} showRecommendations={true} />
                </FlexGrid>
            )}
        </div>
    )
}

export default memo(Portrait)
